/**
 * Search params the /login route understands.
 *
 * `showPwd` reveals the email + password form. It is a VISIBILITY switch, not
 * a security control: the API's PASSWORD_AUTH_ENABLED flag decides whether
 * password sign-in exists at all (production never sets it), and
 * POST /api/auth/sign-in/email works with or without this param. What it buys
 * is a QA login screen that looks like production's by default — Google only —
 * so people testing the real flow aren't nudged toward a test-automation path.
 * Automation that drives the UI opens /login?showPwd=true; automation that
 * uses the API never sees the page at all.
 */
export interface LoginSearch {
  next?: string;
  error?: string;
  error_description?: string;
  /** Present (true) only when the URL explicitly asks for the password form. */
  showPwd?: true;
}

/**
 * TanStack Router's default search parser JSON-decodes values, so
 * `?showPwd=true` arrives as the boolean `true`; a hand-built URL or an
 * encoded `"true"` arrives as the string. Accept exactly those two and nothing
 * else — `?showPwd=1`, `?showPwd=yes` and `?showPwd` all stay hidden, so the
 * switch can't be flipped by a near-miss.
 */
function isShowPwd(value: unknown): boolean {
  return value === true || value === 'true';
}

export function validateLoginSearch(raw: Record<string, unknown>): LoginSearch {
  return {
    next: typeof raw.next === 'string' ? raw.next : undefined,
    error: typeof raw.error === 'string' ? raw.error : undefined,
    error_description:
      typeof raw.error_description === 'string' ? raw.error_description : undefined,
    // Omitted rather than `false` when absent, so it never appears in URLs the
    // page rebuilds for itself.
    ...(isShowPwd(raw.showPwd) ? { showPwd: true as const } : {}),
  };
}
